import React, { useEffect, useState, useMemo } from "react";
import api from "../../lib/api";
import { formatCLP, formatDateTime } from "../../lib/format";
import { ArrowDownLeft, ArrowUpRight, FunnelSimple, X } from "@phosphor-icons/react";

export default function Movements() {
  const [all, setAll] = useState([]);
  const [filters, setFilters] = useState({ type: "", min: "", max: "", from: "", to: "" });

  useEffect(() => {
    api.get("/me/transactions").then((r) => setAll(r.data));
  }, []);

  const filtered = useMemo(() => {
    return all.filter((t) => {
      if (filters.type && t.type !== filters.type) return false;
      if (filters.min && t.amount < Number(filters.min)) return false;
      if (filters.max && t.amount > Number(filters.max)) return false;
      if (filters.from && t.created_at < filters.from) return false;
      if (filters.to && t.created_at > filters.to + "T23:59:59") return false;
      return true;
    });
  }, [all, filters]);

  const clear = () => setFilters({ type: "", min: "", max: "", from: "", to: "" });

  return (
    <div className="space-y-8">
      <header>
        <div className="label-mono mb-2">Movimientos</div>
        <h1 className="font-display text-4xl font-black tracking-tighter">Todo el historial</h1>
      </header>

      <div className="bento p-6">
        <div className="flex items-center gap-2 mb-5">
          <FunnelSimple size={18} weight="duotone" />
          <span className="label-mono">Filtros</span>
          <button onClick={clear} className="ml-auto text-xs text-inkMuted hover:text-forest flex items-center gap-1" data-testid="clear-filters">
            <X size={12} /> Limpiar
          </button>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
          <Field label="Tipo">
            <select data-testid="filter-type" value={filters.type} onChange={(e) => setFilters({ ...filters, type: e.target.value })} className="w-full h-11 border border-hairline bg-surface px-3 text-sm">
              <option value="">Todos</option>
              <option value="credit">Ingreso</option>
              <option value="debit">Egreso</option>
            </select>
          </Field>
          <Field label="Monto mínimo">
            <input data-testid="filter-min" type="number" value={filters.min} onChange={(e) => setFilters({ ...filters, min: e.target.value })} className="w-full h-11 border border-hairline bg-surface px-3 text-sm font-mono" placeholder="0" />
          </Field>
          <Field label="Monto máximo">
            <input data-testid="filter-max" type="number" value={filters.max} onChange={(e) => setFilters({ ...filters, max: e.target.value })} className="w-full h-11 border border-hairline bg-surface px-3 text-sm font-mono" placeholder="∞" />
          </Field>
          <Field label="Desde">
            <input data-testid="filter-from" type="date" value={filters.from} onChange={(e) => setFilters({ ...filters, from: e.target.value })} className="w-full h-11 border border-hairline bg-surface px-3 text-sm font-mono" />
          </Field>
          <Field label="Hasta">
            <input data-testid="filter-to" type="date" value={filters.to} onChange={(e) => setFilters({ ...filters, to: e.target.value })} className="w-full h-11 border border-hairline bg-surface px-3 text-sm font-mono" />
          </Field>
        </div>
      </div>

      <div className="bento">
        <div className="px-6 py-4 border-b border-hairline flex items-center justify-between">
          <h2 className="font-display text-xl font-bold">{filtered.length} movimientos</h2>
          <span className="label-mono">{filters.type || "todos"}</span>
        </div>
        {filtered.length === 0 ? (
          <div className="p-16 text-center text-inkMuted text-sm">Sin movimientos.</div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="label-mono border-b border-hairline">
                <th className="text-left px-6 py-3">Fecha</th>
                <th className="text-left px-6 py-3">Tipo</th>
                <th className="text-left px-6 py-3">Descripción</th>
                <th className="text-left px-6 py-3">Estado</th>
                <th className="text-right px-6 py-3">Monto</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((t) => (
                <tr key={t.id} className="border-b border-hairline hover:bg-bg">
                  <td className="px-6 py-3 font-mono text-xs text-inkMuted">{formatDateTime(t.created_at)}</td>
                  <td className="px-6 py-3">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-mono uppercase tracking-wider ${t.type === "credit" ? "bg-success/10 text-success" : "bg-burnt/10 text-burnt"}`}>
                      {t.type === "credit" ? <ArrowDownLeft size={12} weight="bold" /> : <ArrowUpRight size={12} weight="bold" />}
                      {t.type === "credit" ? "Ingreso" : "Egreso"}
                    </span>
                  </td>
                  <td className="px-6 py-3">{t.description}</td>
                  <td className="px-6 py-3 label-mono">{t.status}</td>
                  <td className={`px-6 py-3 text-right font-mono font-medium ${t.type === "credit" ? "text-success" : "text-ink"}`}>
                    {t.type === "credit" ? "+" : "−"} {formatCLP(t.amount)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="label-mono block mb-1.5">{label}</label>
      {children}
    </div>
  );
}
